import React from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Collapse,
  Input,
  InputNumber,
  Spin,
  Switch,
  Table,
  Tabs,
  Typography,
  Card,
  Space,
} from "antd";
import type { ColumnType } from "antd/es/table";
import { AppstoreOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";

import { API_URL } from "../../shared/utils/Util";
import { assertNever, fetchT, getFetchT } from "../../shared/utils/TypedUtil";
import {
  GetMenuResponse,
  GetActionSchemasResponse,
  GetItemModificationSchemasResponse,
  UpdateItemSchemaResponse,
} from "../../types/api";
import {
  ActionSchema,
  ItemModificationSchema,
  ItemSchema,
  ItemSchemaGroup,
  MenuType,
} from "../../types/model";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { ImageUploadCell } from "./ImageUploadCell";
import { ItemSchemaModal } from "./ItemSchemaModal";
import { ItemSchemaDeleteButton } from "./ItemSchemaDeleteButton";
import { Price } from "../../shared/utils/Price";
import { indexMenuItems } from "../combos/pricing";
import { ComboSchema } from "../combos/types";
import { ComboSchemaModal } from "./ComboSchemaModal";
import { SectionCombos } from "./SectionCombos";
import { useMenuCombos } from "./useMenuCombos";

const { Title, Text } = Typography;

// Natural comparison for SKU keys like "R2" vs "R10" so that R2 sorts before
// R10 (numeric: true compares embedded numbers numerically). Items with a SKU
// sort before those without; ties fall back to the item name.
const compareSku = (a: ItemSchema, b: ItemSchema): number => {
  const sa = a.pos_key ?? "";
  const sb = b.pos_key ?? "";
  if (sa && sb) {
    const cmp = sa.localeCompare(sb, undefined, { numeric: true, sensitivity: "base" });
    if (cmp !== 0) return cmp;
  } else if (sa) {
    return -1;
  } else if (sb) {
    return 1;
  }
  return a.name.localeCompare(b.name);
};

type ItemSchemaGroupListProps = {
  restaurantId: string;
};

export const ItemSchemaGroupList = (props: ItemSchemaGroupListProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { genericError, notifyError } = useNotification();
  const [selectedMenuType, setSelectedMenuType] = React.useState<string>("dine_in");
  const [groups, setGroups] = React.useState<ItemSchemaGroup[] | null>(null);
  const [action_schemas, setActionSchemas] = React.useState<ActionSchema[] | null>(null);
  const [mod_schemas, setModSchemas] = React.useState<ItemModificationSchema[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");
  const [togglingItemIds, setTogglingItemIds] = React.useState<Set<string>>(new Set());

  // Inline price editing: maps item ID -> edited price string (dollar format).
  // Only contains entries for items whose price has been changed from the original.
  const [editedPrices, setEditedPrices] = React.useState<Map<string, string>>(new Map());
  const [savingPriceIds, setSavingPriceIds] = React.useState<Set<string>>(new Set());

  // Combos live in the menu sections next to items.
  const menuCombos = useMenuCombos(props.restaurantId);
  const [editingCombo, setEditingCombo] = React.useState<{
    combo: ComboSchema;
    menuType: MenuType;
  } | null>(null);

  const refreshMenu = () => {
    setLoading(true);
    getFetchT<GetMenuResponse>(
      `${API_URL}/v1/r/${props.restaurantId}/menu`,
      (result: GetMenuResponse) => {
        setGroups(result.item_schema_groups);
        setLoading(false);
      },
      (err) => {
        genericError();
        setLoading(false);
      }
    );
  };

  // Reconstruct full action/modification schema objects from ID arrays.
  // The form only submits `_ids` arrays; we need the full objects so that
  // subsequent modal opens and toggle/save handlers see correct data.
  const resolveSchemaRelations = (schema: ItemSchema): ItemSchema => {
    const resolved = { ...schema };
    if (resolved.action_schema_ids && action_schemas) {
      const idSet = new Set(resolved.action_schema_ids);
      resolved.action_schemas = action_schemas.filter((a) => idSet.has(a.id));
    }
    if (resolved.modification_schema_ids && mod_schemas) {
      const idSet = new Set(resolved.modification_schema_ids);
      resolved.modification_schemas = mod_schemas.filter((m) => idSet.has(m.id));
    }
    return resolved;
  };

  // Optimistic update: replace an item in local state without re-fetching.
  const handleItemUpdated = (updatedSchema: ItemSchema) => {
    const resolved = resolveSchemaRelations(updatedSchema);
    setGroups((prev) => {
      if (!prev) return prev;
      return prev.map((group) => ({
        ...group,
        item_schemas: group.item_schemas.map((item) => (item.id === resolved.id ? resolved : item)),
      }));
    });
  };

  // Optimistic delete: remove an item from local state without re-fetching.
  const handleItemDeleted = (deletedItemId: string) => {
    setGroups((prev) => {
      if (!prev) return prev;
      return prev.map((group) => ({
        ...group,
        item_schemas: group.item_schemas.filter((item) => item.id !== deletedItemId),
      }));
    });
  };

  // Optimistic create: insert a new item into the correct group without re-fetching.
  const handleItemCreated = (newSchema: ItemSchema) => {
    const resolved = resolveSchemaRelations(newSchema);
    setGroups((prev) => {
      if (!prev) return prev;
      return prev.map((group) =>
        group.id === resolved.item_schema_group_id
          ? { ...group, item_schemas: [...group.item_schemas, resolved] }
          : group
      );
    });
  };

  // Load the menu.
  React.useEffect(refreshMenu, [props.restaurantId]);

  // Load action and modification schemas.
  React.useEffect(() => {
    getFetchT<GetActionSchemasResponse>(
      `${API_URL}/v1/r/${props.restaurantId}/action_schemas`,
      (result: GetActionSchemasResponse) => {
        setActionSchemas(result.schemas);
      },
      (err) => {
        genericError();
      }
    );
  }, [props.restaurantId]);

  React.useEffect(() => {
    getFetchT<GetItemModificationSchemasResponse>(
      `${API_URL}/v1/r/${props.restaurantId}/item_modification_schemas`,
      (result: GetItemModificationSchemasResponse) => {
        setModSchemas(result.schemas);
      },
      (err) => {
        genericError();
      }
    );
  }, [props.restaurantId]);

  // Inline sold-out toggle handler.
  const handleSoldOutToggle = (record: ItemSchema, checked: boolean) => {
    setTogglingItemIds((prev) => new Set(prev).add(record.id));

    const updatedSchema: ItemSchema = {
      ...record,
      sold_out: checked,
    };

    if (updatedSchema.action_schemas) {
      updatedSchema.action_schema_ids = updatedSchema.action_schemas.map((a) => a.id);
    }
    if (updatedSchema.modification_schemas) {
      updatedSchema.modification_schema_ids = updatedSchema.modification_schemas.map((m) => m.id);
    }

    fetchT<UpdateItemSchemaResponse>(
      "PUT",
      `${API_URL}/v1/r/${props.restaurantId}/item_schemas/${record.id}`,
      updatedSchema,
      () => {
        setTogglingItemIds((prev) => {
          const next = new Set(prev);
          next.delete(record.id);
          return next;
        });
        setGroups((prevGroups) => {
          if (!prevGroups) return prevGroups;
          return prevGroups.map((group) => ({
            ...group,
            item_schemas: group.item_schemas.map((item) =>
              item.id === record.id ? { ...item, sold_out: checked } : item
            ),
          }));
        });
      },
      (err: unknown) => {
        setTogglingItemIds((prev) => {
          const next = new Set(prev);
          next.delete(record.id);
          return next;
        });
        notifyError(t("error"), err);
      }
    );
  };

  // Handle inline price edit for a single item (tracks dirty state while typing).
  const handlePriceEdit = (
    itemId: string,
    newPriceStr: string | null,
    originalPriceCents: number
  ) => {
    setEditedPrices((prev) => {
      const next = new Map(prev);
      const originalStr = Price.fromCents(originalPriceCents).toString();
      if (newPriceStr === null || newPriceStr === "" || newPriceStr === originalStr) {
        next.delete(itemId);
      } else {
        next.set(itemId, newPriceStr);
      }
      return next;
    });
  };

  // Auto-save a single item's price on blur or Enter.
  const handlePriceSave = (record: ItemSchema) => {
    const newPriceStr = editedPrices.get(record.id);
    if (newPriceStr === undefined) return;

    setSavingPriceIds((prev) => new Set(prev).add(record.id));

    const newPriceCents = Price.fromString(newPriceStr).toCents();
    const updatedSchema: ItemSchema = { ...record, price: newPriceCents };

    if (updatedSchema.action_schemas) {
      updatedSchema.action_schema_ids = updatedSchema.action_schemas.map((a) => a.id);
    }
    if (updatedSchema.modification_schemas) {
      updatedSchema.modification_schema_ids = updatedSchema.modification_schemas.map((m) => m.id);
    }

    fetchT<UpdateItemSchemaResponse>(
      "PUT",
      `${API_URL}/v1/r/${props.restaurantId}/item_schemas/${record.id}`,
      updatedSchema,
      () => {
        setSavingPriceIds((prev) => {
          const next = new Set(prev);
          next.delete(record.id);
          return next;
        });
        setEditedPrices((prev) => {
          const next = new Map(prev);
          next.delete(record.id);
          return next;
        });
        setGroups((prevGroups) => {
          if (!prevGroups) return prevGroups;
          return prevGroups.map((g) => ({
            ...g,
            item_schemas: g.item_schemas.map((item) =>
              item.id === record.id ? { ...item, price: newPriceCents } : item
            ),
          }));
        });
      },
      (err: unknown) => {
        setSavingPriceIds((prev) => {
          const next = new Set(prev);
          next.delete(record.id);
          return next;
        });
        notifyError(t("error"), err);
      }
    );
  };

  if (
    groups === null ||
    action_schemas === null ||
    mod_schemas === null ||
    menuCombos.combos === null
  ) {
    // Still loading (first mount only)
    return <Spin size="large" />;
  }

  const buildGroupCollapseItem = (group: ItemSchemaGroup, menuType: MenuType) => {
    const columns: ColumnType<ItemSchema>[] = [
      {
        title: t("image"),
        dataIndex: "image_manifest",
        width: 80,
        render: (_: unknown, record: ItemSchema) => (
          <ImageUploadCell
            restaurantId={props.restaurantId}
            record={record}
            onSuccess={handleItemUpdated}
          />
        ),
      },
      {
        title: t("menu_item_name"),
        dataIndex: "name",
        sortDirections: ["ascend", "descend"],
        sorter: (a: ItemSchema, b: ItemSchema) => {
          return a.name.localeCompare(b.name);
        },
        render: (_: unknown, record: ItemSchema) => (
          <Space size={4}>
            {record.pos_key ? <Text strong>{record.pos_key}</Text> : null}
            <ItemSchemaModal
              restaurantId={props.restaurantId}
              menu_type={menuType}
              item_schema={record}
              is_new={false}
              action_schemas={action_schemas}
              item_modification_schemas={mod_schemas}
              on_update_success={(updated) => handleItemUpdated(updated)}
            />
          </Space>
        ),
      },
      {
        title: t("price"),
        dataIndex: "price",
        width: 130,
        sorter: (a: ItemSchema, b: ItemSchema) => a.price - b.price,
        render: (_: unknown, record: ItemSchema) => {
          const editedValue = editedPrices.get(record.id);
          const currentValue = editedValue ?? Price.fromCents(record.price).toString();
          const isSaving = savingPriceIds.has(record.id);
          return (
            <InputNumber<string>
              min="0"
              step="0.01"
              precision={2}
              stringMode
              value={currentValue}
              onChange={(val) => handlePriceEdit(record.id, val, record.price)}
              onBlur={() => handlePriceSave(record)}
              onPressEnter={() => handlePriceSave(record)}
              disabled={isSaving}
              prefix="$"
              size="small"
              style={{ width: 110 }}
              status={isSaving ? "warning" : undefined}
            />
          );
        },
      },
      {
        title: t("sold_out"),
        dataIndex: "sold_out",
        width: 100,
        render: (_: unknown, record: ItemSchema) => (
          <Switch
            checked={!!record.sold_out}
            loading={togglingItemIds.has(record.id)}
            onChange={(checked) => handleSoldOutToggle(record, checked)}
            size="small"
          />
        ),
      },
      {
        title: "",
        width: 80,
        align: "center" as const,
        render: (_: unknown, record: ItemSchema) => (
          <ItemSchemaDeleteButton
            restaurantId={props.restaurantId}
            item_schema={record}
            on_delete_success={() => {
              handleItemDeleted(record.id);
              // Deleting an item cascades into combos and add-on tiers.
              menuCombos.refresh();
            }}
          />
        ),
      },
    ];

    // Filter items by search text, then sort by SKU so the default order is
    // SKU-based (R2 before R10). Users can still re-sort by name or price.
    const filteredItems = (group.item_schemas || [])
      .filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()))
      .sort(compareSku);

    const sectionCombos = (menuCombos.combos ?? [])
      .filter((c) => c.item_schema_group_id === group.id)
      .sort((a, b) => a.seq - b.seq);
    const menuTypeItems = indexMenuItems(groups.filter((g) => g.menu_type === menuType));

    // Empty combo for adding a combo to this group, placed after its combos.
    const newComboTemplate: ComboSchema = {
      item_schema_group_id: group.id,
      name: "",
      notes: "",
      sold_out: false,
      featured: false,
      seq: sectionCombos.length === 0 ? 0 : Math.max(...sectionCombos.map((c) => c.seq)) + 1,
      entrees: [],
      addons: [],
    };

    // Empty schema for adding an item to this group.
    const new_schema_template: ItemSchema = {
      item_schema_group_id: group.id,
      id: "its_Temporary_IdForNewItem",
      name: "",
      price: 0,
      tax_free: false,
      shippable: false,
    };

    return {
      key: group.id,
      label: (
        <Space>
          <AppstoreOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
          <Title level={4} style={{ margin: 0 }}>
            {group.name}
          </Title>
        </Space>
      ),
      extra: (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button
            icon={<PlusOutlined />}
            onClick={() => setEditingCombo({ combo: newComboTemplate, menuType })}
            style={{ borderRadius: token.borderRadiusLG, height: token.controlHeight }}
          >
            {t("combo_add")}
          </Button>
          <ItemSchemaModal
            restaurantId={props.restaurantId}
            item_schema={new_schema_template}
            menu_type={menuType}
            is_new={true}
            action_schemas={action_schemas}
            item_modification_schemas={mod_schemas}
            on_update_success={(created) => handleItemCreated(created)}
          />
        </Space>
      ),
      children: (
        <>
          {sectionCombos.length > 0 ? (
            <SectionCombos
              restaurantId={props.restaurantId}
              combos={sectionCombos}
              addons={menuCombos.addons}
              items={menuTypeItems}
              pointsConfig={menuCombos.pointsConfig}
              busyIds={menuCombos.busyIds}
              onEdit={(combo) => setEditingCombo({ combo, menuType })}
              onSoldOut={menuCombos.setSoldOut}
              onMove={(from, to) => menuCombos.moveCombo(sectionCombos, from, to)}
              onImage={(combo, manifest) =>
                menuCombos.saveCombo({ ...combo, image_manifest: manifest })
              }
              onDelete={menuCombos.removeCombo}
            />
          ) : null}
          <Table
            columns={columns}
            dataSource={filteredItems}
            rowKey={(record: ItemSchema) => record.id}
            loading={loading}
            pagination={false}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        </>
      ),
    };
  };

  const menuTypes: ReadonlyArray<MenuType> = [...new Set(groups.map((g) => g.menu_type))];
  return (
    <div>
      {/* Menu Types Tabs */}
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Tabs
          activeKey={selectedMenuType}
          onChange={(newValue) => {
            if (newValue != null) {
              setSelectedMenuType(newValue);
            }
          }}
          size="large"
          items={menuTypes.map((menuType) => {
            let displayName: string = "";
            switch (menuType) {
              case "dine_in":
                displayName = t("dine_in_pickup");
                break;
              case "large_volume":
                displayName = t("group_order");
                break;
              default:
                assertNever(menuType);
            }

            return {
              key: menuType,
              label: displayName,
              children: (
                <>
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder={t("search_by_name")}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    style={{ marginTop: token.marginMD, maxWidth: 300 }}
                  />
                  <Collapse
                    defaultActiveKey={groups
                      .filter((g) => g.menu_type === menuType)
                      .map((g) => g.id)}
                    style={{ marginTop: token.marginLG }}
                    items={groups
                      .filter((g) => g.menu_type === menuType)
                      .map((g) => buildGroupCollapseItem(g, menuType))}
                  />
                </>
              ),
            };
          })}
        />
      </Card>
      <ComboSchemaModal
        open={editingCombo !== null}
        restaurantId={props.restaurantId}
        combo={editingCombo?.combo ?? newComboPlaceholder}
        menuType={editingCombo?.menuType ?? "dine_in"}
        groups={groups}
        addons={menuCombos.addons}
        pointsConfig={menuCombos.pointsConfig}
        onClose={() => setEditingCombo(null)}
        onSaved={(saved) => {
          setEditingCombo(null);
          menuCombos.replaceLocal(saved);
          // The add-ons' used_by changed.
          menuCombos.refreshAddons();
        }}
        onAddonCreated={menuCombos.addonCreated}
      />
    </div>
  );
};

const newComboPlaceholder: ComboSchema = {
  item_schema_group_id: "",
  name: "",
  notes: "",
  sold_out: false,
  featured: false,
  seq: 0,
  entrees: [],
  addons: [],
};
